import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllArtworks } from '../data/artworkData';
import { scrollToTop } from '../utils/helpers';
import { scrollRevealAnimation, sectionTitleAnimation, galleryGridAnimation, staggeredCardReveal } from '../utils/animations';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const BestImagesSection = () => {
  const navigate = useNavigate();
  const allArtworks = getAllArtworks();
  
  // Function to get random artworks from all categories
  const getRandomArtworks = (artworks, count) => {
    const shuffled = [...artworks].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };
  
  const displayedArtworks = getRandomArtworks(allArtworks, 6);
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const gridRef = useRef(null);
  const featuredImageRef = useRef(null);
  const featuredTitleRef = useRef(null);
  const featuredDescRef = useRef(null);

  useEffect(() => {
    console.log('BestImagesSection: Component mounted, initializing scroll animations');
    const timer = setTimeout(() => {
      // Animate the section title
      if (titleRef.current) {
        sectionTitleAnimation(titleRef);
      }
      
      // Animate featured sculpture when it enters viewport
      const featuredImage = featuredImageRef.current;
      const featuredTitle = featuredTitleRef.current;
      const featuredDesc = featuredDescRef.current;

      if (featuredImage) {
        gsap.set([featuredImage, featuredTitle, featuredDesc], { opacity: 0, y: 30 });

        gsap.to([featuredImage, featuredTitle, featuredDesc], {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
          stagger: 0.2,
          scrollTrigger: {
            trigger: featuredImage,
            start: "top 90%", // Trigger when section is 90% in viewport
            toggleActions: "play none none none",
            once: true // Only animate once
          }
        });
      }
      
      // Animate the gallery grid
      if (gridRef.current) {
        staggeredCardReveal(gridRef);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const handleShowMore = () => {
    navigate('/gallery');
    // Scroll to top when navigating to gallery
    setTimeout(() => {
      scrollToTop();
    }, 100);
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 ref={titleRef} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Featured Sculptures</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover our finest handmade sculptures, custom statues, and artistic installations from Ram Tirath Art
          </p>
        </div>

        {/* Featured Ram Sculpture - Highlighted */}
        <div className="mb-16">
          <div ref={featuredTitleRef} className="text-center mb-8">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Divine Ram Sculpture
            </h3>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto mb-3 rounded-full"></div>
            <p ref={featuredDescRef} className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto">
              Handcrafted masterpiece showcasing divine artistry and traditional craftsmanship
            </p>
          </div>

          <div className="flex justify-center">
            <div 
              ref={featuredImageRef}
              className="relative max-w-3xl"
            >
              <img
                src="/images/ram-sculpture.png"
                alt="Divine Ram Sculpture - Handcrafted Premium Art by Ram Tirath Art"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="border-t-2 border-orange-200"></div>
          <div className="text-center py-6">
            <h3 className="text-2xl font-semibold text-gray-800">More From Our Collection</h3>
          </div>
        </div>
        
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedArtworks.map((artwork, index) => (
            <div key={index} className="artwork-frame card group relative overflow-hidden rounded-lg bg-white p-4 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="aspect-square overflow-hidden rounded-lg bg-gray-100 relative">
                <img
                  src={artwork.image}
                  alt={artwork.title || `Artwork ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  style={{ 
                    objectPosition: 'center center',
                    minHeight: '100%',
                    minWidth: '100%'
                  }}
                  onError={(e) => {
                    e.target.src = '/images/placeholder.jpg';
                  }}
                />
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-gray-800">{artwork.title || `Artwork ${index + 1}`}</h3>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <button
            onClick={handleShowMore}
            className="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Show More
          </button>
        </div>
      </div>
    </section>
  );
};

export default BestImagesSection;
